/**
 * The one reading of "can this contact be a payer" for the live processor (OWA-1574, PAY-099).
 *
 * `paymentApi.paymentEligibilityInfo` and `Payrix.paymentEligible` each re-implemented the same
 * checks, and the two Rust ports already diverged from the client contract. Both now ask
 * {@link contactPaymentFacts}; Payrix.ts is the processor whose rules these are. (LawPay's copy
 * goes with the LawPay clean-up.)
 */
import store from '@/store/store';
import {isCasePayer} from '@/Case/caseParties';
import type Contact from '@/entities/contact';
import type Lead from '@/entities/lead';
import type Matter from '@/entities/matter';

export type PaymentEligibilityInfo = {
    isPayer: boolean,
    hasFullName: boolean,
    hasEmail: boolean,
    hasAddress: boolean,
    hasPhone: boolean,
}

/**
 * What a contact has for paying: a client or benefactor of the Case or Lead (OWA-1341:
 * caseParties, never the lookup key), a full name (a company needs a last name, a person a first
 * and last) that is not deleted, and a primary contactable email, address and phone.
 *
 * @param {Contact} contact
 * @param {Lead | Matter} unsavedEntity - a Case or Lead not yet in the store
 * @returns {PaymentEligibilityInfo}
 */
export function contactPaymentFacts(contact: Contact, unsavedEntity?: Lead | Matter): PaymentEligibilityInfo {
    const bus = contact.isCompany;
    const hasFullName = !!(((bus && contact.last) || (!bus && (contact.first && contact.last))) && contact.status !== 'D');
    const state = store.getState();
    const primary = (r: {entityId: string, isPrimary?: boolean, canContact?: boolean}) => r.isPrimary && r.canContact;

    return {
        isPayer: isCasePayer(contact.id, unsavedEntity),
        hasFullName,
        hasEmail: !!state.resources.find(r => r.entityId === contact.id && r.type === 'email' && primary(r)),
        hasAddress: !!state.addresses.find(a => a.entityId === contact.id && primary(a)),
        hasPhone: !!state.resources.find(r => r.entityId === contact.id && r.type === 'phone' && primary(r)),
    };
}

/** Every fact true. */
export function isPaymentEligible(facts: PaymentEligibilityInfo): boolean {
    return facts.isPayer && facts.hasFullName && facts.hasEmail && facts.hasAddress && facts.hasPhone;
}
